import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { ShieldCheckIcon } from 'lucide-react'
import { ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, TabbedWidgetWrapper, WidgetInputToggleField } from '../../rapid-ui'
import { ColorProvider } from '../../views'
import { CanvasData } from '../canvas/data'
import {
	campaignDetailsBrandingAtom,
	feedAdAiImageryDetectedAtom,
	feedAdCanvasLoadCounterAtom,
	feedAdMetaCanvasDocumentAtom,
	feedAdMetaCanvasEnabledAtom,
	feedAdMetaForceAiGeneratedLabelAtom,
	showCanvasToolbarAtom,
} from '../state'
import { AiGeneratedLabelToggle } from './ai-generated-label-toggle'
import { CanvasExportDialog, CanvasImportDialog } from './canvas-document-dialogs'
import { DesignFilmstrip } from './design-filmstrip'
import { FeedAdImageWidget } from './feed-ad-image-widget'
import { FeedAdLayerManager } from './feed-ad-layer-manager'

const fallbackPalette = {
	foreground: 'rgba(255,255,255,1)',
	background: 'rgba(0,0,0,1)',
	accentForeground: 'rgba(255,255,255,1)',
	accentBackground: 'rgba(0,0,0,1)',
}

export type FeedAdWidgetChildrenHelpers = {
	widget: ReactNode
}

export interface FeedAdWidgetProps {
	children: (helpers: FeedAdWidgetChildrenHelpers) => ReactNode
	globalTools: ReactNode
}

export function FeedAdWidget(
	{ children, globalTools }: FeedAdWidgetProps,
) {
	const { t } = useTranslation('common')
	const branding = useAtomValue(campaignDetailsBrandingAtom)
	const [canvasEnabled, setCanvasEnabled] = useAtom(feedAdMetaCanvasEnabledAtom)
	const canvasDocument = useAtomValue(feedAdMetaCanvasDocumentAtom)
	const setCanvasDocument = useSetAtom(feedAdMetaCanvasDocumentAtom)
	const setLoadCounter = useSetAtom(feedAdCanvasLoadCounterAtom)
	const [forceAiGeneratedLabel, setForceAiGeneratedLabel] = useAtom(feedAdMetaForceAiGeneratedLabelAtom)
	const aiImageryDetected = useAtomValue(feedAdAiImageryDetectedAtom)

	const showCanvasTools = useAtomValue(showCanvasToolbarAtom)

	const [exportOpen, setExportOpen] = useState(false)
	const [importOpen, setImportOpen] = useState(false)

	const handleImport = useCallback((doc: CanvasData) => {
		setCanvasDocument(JSON.stringify(doc))
		setLoadCounter((c) => c + 1)
	}, [setCanvasDocument, setLoadCounter])

	const tabs = [
		{
			id: 'image',
			label: t('editor.widgets.image'),
			children: <FeedAdImageWidget />,
		},
		...(!canvasEnabled
			? [{
				id: 'layers',
				label: t('editor.widgets.layers'),
				children: <FeedAdLayerManager />,
			}]
			: []),
		...(canvasEnabled
			? [{
				id: 'designs',
				label: 'Designs',
				children: (
					<DesignFilmstrip
						onApplyDesign={(doc) => {
							setCanvasDocument(doc)
							setLoadCounter((c) => c + 1)
						}}
					/>
				),
			}]
			: []),
		{
			id: 'tools',
			label: t('editor.widgets.tools'),
			children: (
				<>
					{globalTools}
					<div className="flex flex-col divide-y divide-grey-100 mt-3 text-left">
						<AiGeneratedLabelToggle
							forced={forceAiGeneratedLabel ?? false}
							onForcedChange={setForceAiGeneratedLabel}
							detected={aiImageryDetected}
						/>
						{showCanvasTools && (
							<div className="py-2">
								<label className="flex items-center gap-2">
									<WidgetInputToggleField
										value={canvasEnabled ?? false}
										onChange={(value) => setCanvasEnabled(value)}
									/>
									<ShieldCheckIcon className="size-3 text-grey-400" />
									{t('editor.widgets.canvasMode')} (experimental early preview)
								</label>
								{canvasEnabled && (
									<div className="flex items-center gap-2 mt-1 pl-12">
										<Button type="subtle" size="tiny" onClick={() => setExportOpen(true)}>
											Export JSON
										</Button>
										<Button type="subtle" size="tiny" onClick={() => setImportOpen(true)}>
											Import JSON
										</Button>
									</div>
								)}
							</div>
						)}
					</div>
				</>
			),
		},
	]
	const [currentTab, setCurrentTab] = useState<typeof tabs[number]['id']>('image')
	const effectiveTab = currentTab === 'layers' && canvasEnabled ? 'image' : currentTab

	const widget = (
		<>
			<TabbedWidgetWrapper
				value={effectiveTab}
				onChange={(v) => setCurrentTab(v)}
				tabs={tabs}
				name={t('editor.widgets.feedAd')}
				animateHeight={false}
			/>
		</>
	)

	return (
		<ColorProvider brandingPalette={branding ?? fallbackPalette}>
			{children({ widget })}
			<CanvasExportDialog open={exportOpen} onClose={() => setExportOpen(false)} document={canvasDocument} />
			<CanvasImportDialog open={importOpen} onClose={() => setImportOpen(false)} onLoad={handleImport} />
		</ColorProvider>
	)
}
